// Display-only timestamp formatting. Stored values stay UTC ISO; this renders them in the
// account's configured display zone (default America/New_York, DST-aware → EDT/EST), e.g.
// "Jun 8, 2026, 7:03 PM EDT". The console uses vitest (not byte-pinned static tests), so we
// format with Intl directly rather than the pilot's hand-rolled deterministic formatter.

const PLACEHOLDER = "—";

export function formatDisplayTime(iso: string | null | undefined, timeZone: string): string {
  if (typeof iso !== "string" || iso.length === 0) {
    return PLACEHOLDER;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return PLACEHOLDER;
  }
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone,
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    }).format(date);
  } catch {
    // Invalid timeZone — fall back to a zone-less label rather than throwing.
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  }
}

/** Compact relative label for live CAD rails; falls back to full display time after 24h. */
export function formatRelativeTime(
  iso: string | null | undefined,
  nowMs: number,
  timeZone: string,
): string {
  if (typeof iso !== "string" || iso.length === 0) {
    return PLACEHOLDER;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return PLACEHOLDER;
  }
  const deltaMs = nowMs - date.getTime();
  if (deltaMs < 0) {
    return formatDisplayTime(iso, timeZone);
  }
  const seconds = Math.floor(deltaMs / 1000);
  if (seconds < 45) {
    return "Just now";
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  return formatDisplayTime(iso, timeZone);
}

/** Check-in popover: relative plus absolute local time when both are known. */
export function formatCheckInWhen(
  iso: string | null | undefined,
  nowMs: number,
  timeZone: string,
): string {
  const relative = formatRelativeTime(iso, nowMs, timeZone);
  const absolute = formatDisplayTime(iso, timeZone);
  if (relative === PLACEHOLDER || absolute === PLACEHOLDER) {
    return PLACEHOLDER;
  }
  if (relative === absolute) {
    return absolute;
  }
  return `${relative} · ${absolute}`;
}
